import { useEffect } from 'react';
import { GeoJSON } from 'react-leaflet';
import type L from 'leaflet';
import type { Feature } from 'geojson';
import { useObservable } from '@/shared/hooks/useObservable';
import { getMapLayers } from '../api/map-layers.api';
import type { MapLayer } from '../api/map-layers.api';
import { mapLayersService } from '../services/map-layers.service';
import { useKmlLayer } from '../hooks/useKmlLayer';

function kmlStyle(feature?: Feature): L.PathOptions {
  const p = feature?.properties ?? {};
  return {
    fillColor: (p['fill'] as string) || '#3388ff',
    fillOpacity: (p['fill-opacity'] as number) ?? 0.4,
    color: (p['stroke'] as string) || '#3388ff',
    opacity: (p['stroke-opacity'] as number) ?? 1,
    weight: (p['stroke-width'] as number) ?? 2,
  };
}

function onEachFeature(feature: Feature, leafletLayer: L.Layer) {
  const p = feature.properties ?? {};
  const name = p['name'] as string | undefined;
  const rawDescription = p['description'];
  const description: string | undefined =
    rawDescription && typeof rawDescription === 'object' && '@type' in rawDescription
      ? (rawDescription as { '@type': string; value: string }).value
      : typeof rawDescription === 'string' ? rawDescription : undefined;
  if (!name && !description) return;

  const popup = document.createElement('div');
  popup.style.cssText = 'max-height:240px;overflow:auto;font-size:12px;min-width:180px';
  if (name) {
    const title = document.createElement('strong');
    title.textContent = name;
    title.style.cssText = 'display:block;margin-bottom:4px;font-size:13px';
    popup.appendChild(title);
  }
  if (description) {
    const body = document.createElement('div');
    body.innerHTML = description;
    popup.appendChild(body);
  }
  (leafletLayer as L.Path).bindPopup(popup, { maxWidth: 320 });
}

function KmlSingleLayer({ layer }: { layer: MapLayer }) {
  const geojson = useKmlLayer(layer.active_file_url!);
  if (!geojson) return null;
  return (
    <GeoJSON
      key={layer.id}
      data={geojson}
      style={kmlStyle}
      onEachFeature={onEachFeature}
    />
  );
}

export function KmlLayersOverlay() {
  const layers = useObservable(mapLayersService.layers$, []);
  const visible = useObservable(mapLayersService.visible$, {});

  useEffect(() => {
    const sub = getMapLayers().subscribe({
      next: (data) => mapLayersService.setLayers(data),
    });
    return () => sub.unsubscribe();
  }, []);

  return (
    <>
      {layers
        .filter(l => l.has_file && visible[l.id] !== false)
        .map(layer => (
          <KmlSingleLayer key={layer.id} layer={layer} />
        ))}
    </>
  );
}
